import { authClient } from './authApi';
import type { User } from './authApi';

export interface UserCreate {
  username: string;
  email: string;
  password?: string;
  role: 'admin' | 'user';
  expiration_date?: string;
}

export interface UserUpdate {
  email?: string;
  role?: 'admin' | 'user';
  is_active?: boolean;
  expiration_date?: string | null;
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  per_page: number;
}

export interface InviteTokenResponse {
  invite_url: string;
  expires_at: string;
  user_id: string;
}

export interface ResetPasswordResponse {
  temporary_password: string;
  message: string;
}

export type { User };

export const adminAPI = {
  async listUsers(
    page: number = 1,
    perPage: number = 20,
    role?: string,
    isActive?: boolean
  ): Promise<UserListResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());
    if (role) params.append('role', role);
    if (isActive !== undefined) params.append('is_active', isActive.toString());

    const response = await authClient.get<UserListResponse>(
      `/admin/users?${params.toString()}`
    );
    return response.data;
  },

  async getUser(userId: string): Promise<User> {
    const response = await authClient.get<User>(`/admin/users/${userId}`);
    return response.data;
  },

  async createUser(userData: UserCreate): Promise<User> {
    const response = await authClient.post<User>('/admin/users', userData);
    return response.data;
  },

  async updateUser(userId: string, userData: UserUpdate): Promise<User> {
    const response = await authClient.put<User>(
      `/admin/users/${userId}`,
      userData
    );
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await authClient.delete(`/admin/users/${userId}`);
  },

  async generateInvite(userId: string): Promise<InviteTokenResponse> {
    const response = await authClient.post<InviteTokenResponse>(
      `/admin/users/${userId}/invite`
    );
    return response.data;
  },

  async resetPassword(userId: string): Promise<ResetPasswordResponse> {
    const response = await authClient.post<ResetPasswordResponse>(
      `/admin/users/${userId}/reset-password`
    );
    return response.data;
  },

  async extendExpiration(
    userId: string,
    expirationDate: string
  ): Promise<{ message: string; new_expiration: string; username: string }> {
    const response = await authClient.post(
      `/admin/users/${userId}/extend-expiration`,
      null,
      { params: { expiration_date: expirationDate } }
    );
    return response.data;
  },
};

export default adminAPI;
